const REFRESH_INTERVAL_MS = 2500;

function App() {
  // Per-user preferences (panel order, density): synced with the backend so
  // they follow the user across devices, not just this browser's localStorage.
  // localStorage stays as an instant-apply cache / offline fallback.
  const [density, setDensityState] = React.useState('compact');
  const [panelOrder, setPanelOrderState] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('panelOrder') || '[]'); } catch { return []; }
  });
  const prefsWriteTimers = React.useRef({});
  function writePreference(key, value) {
    clearTimeout(prefsWriteTimers.current[key]);
    prefsWriteTimers.current[key] = setTimeout(() => {
      fetch('/users/me/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: value }),
      }).catch(() => {});
    }, 800);
  }
  function setDensity(v) {
    setDensityState(v);
    writePreference('density', v);
  }
  function setPanelOrder(order) {
    setPanelOrderState(order);
    try { localStorage.setItem('panelOrder', JSON.stringify(order)); } catch (_) {}
    writePreference('panel_order', order);
  }

  const [user, setUser]         = React.useState(null);
  const [authChecked, setAuthChecked] = React.useState(false);
  const [section, setSection]   = React.useState('monitor');
  const [clients, setClients]   = React.useState([]);
  const [events, setEvents]     = React.useState([]);
  const [commandsByClient, setCommandsByClient] = React.useState({});
  const [selected, setSelected] = React.useState(null);
  const [categoryFilter, setCategoryFilter] = React.useState(null);
  const [panelFilter, setPanelFilter] = React.useState(null);
  const [search, setSearch]     = React.useState('');

  const [liveState, setLiveState] = React.useState('live');
  const [lastUpdate, setLastUpdate] = React.useState(Date.now());
  const [lastCmdResult, setLastCmdResult] = React.useState(null);
  const [notificationCounts, setNotificationCounts] = React.useState({ alarm: 0, trouble: 0, supervisory: 0 });
  const [newAlarmsByClient, setNewAlarmsByClient] = React.useState({});
  const [countsByClient, setCountsByClient] = React.useState({});
  const [lastPanelOkByPanel, setLastPanelOkByPanel] = React.useState({});
  const [animationGen, setAnimationGen] = React.useState(0);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const isMobile = useIsMobile();
  // 'auto' follows viewport; 'simple'/'full' are explicit user overrides (either direction, any width).
  const [viewPref, setViewPref] = React.useState('auto');
  const showSimplified = viewPref === 'simple' || (viewPref === 'auto' && isMobile);
  const initialLoaded = React.useRef(false);
  const shouldAnimate = React.useRef(false);
  const audioCtx = React.useRef(null);
  const lastPanelOkRef = React.useRef({});
  const pairingRef = React.useRef({}); // the last /events/notification-state, by panel

  function weekWindowStart() {
    const d = new Date();
    d.setMinutes(0, 0, 0);      // truncate to current hour
    d.setDate(d.getDate() - 7); // go back exactly 7 days
    return d;
  }

  function playBeep() {
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.25);
    } catch (_) {}
  }

  // Sync tab title with notification badge totals
  React.useEffect(() => {
    const total = (notificationCounts.alarm || 0) + (notificationCounts.trouble || 0) + (notificationCounts.supervisory || 0);
    document.title = total > 0 ? `(${total}) BControl` : 'BControl';
  }, [notificationCounts]);

  // Complete magic-link sign-in (if this load is the link target), then check existing session
  React.useEffect(() => {
    async function boot() {
      if (window.fbAuth.isSignInWithEmailLink(window.location.href)) {
        let emailForSignIn = window.localStorage.getItem('emailForSignIn');
        if (!emailForSignIn) {
          emailForSignIn = window.prompt('Confirma tu correo para completar el acceso:');
        }
        if (emailForSignIn) {
          try {
            const result = await window.fbAuth.signInWithEmailLink(emailForSignIn, window.location.href);
            window.localStorage.removeItem('emailForSignIn');
            const idToken = await result.user.getIdToken();
            await fetch('/auth/firebase-login', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id_token: idToken }),
            });
          } catch (_) {
            // Invalid/expired link — falls through to normal login screen below.
          }
        }
        window.history.replaceState({}, '', '/');
      }

      fetch('/auth/me')
        .then(r => r.ok ? r.json() : null)
        .then(u => { setUser(u); setAuthChecked(true); })
        .catch(() => setAuthChecked(true));
    }
    boot();
  }, []);

  // Pull synced preferences on login. localStorage values (already applied
  // above) stay in place if this fails or a key is simply absent server-side.
  React.useEffect(() => {
    if (!user) return;
    fetch('/users/me/preferences')
      .then(r => r.ok ? r.json() : {})
      .then(p => {
        if (p.density) setDensityState(p.density);
        if (Array.isArray(p.panel_order) && p.panel_order.length > 0) {
          setPanelOrderState(p.panel_order);
          try { localStorage.setItem('panelOrder', JSON.stringify(p.panel_order)); } catch (_) {}
        }
      })
      .catch(() => {});
  }, [user]);

  // Poll clients + initial log load when logged in
  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function pollClients() {
      try {
        const res = await fetch('/clients');
        if (!res.ok) { setLiveState('offline'); return; }
        const data = await res.json();
        if (cancelled) return;
        setClients(data);
        setLastUpdate(Date.now());
        setLiveState('live');
        setSelected(prev => prev || (data[0]?.client_id ?? null));
      } catch (_) {
        if (!cancelled) setLiveState('offline');
      }
    }

    async function loadInitialLogs() {
      try {
        const since = weekWindowStart().toISOString();
        const res = await fetch(`/logs?since=${encodeURIComponent(since)}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) {
          // /logs marks the alerts whose restore came (`restored`); the
          // notification state, if it came first, marks the ones since.
          const winStart = weekWindowStart();
          setEvents(markRestored(data, pairingRef.current).filter(e => keepEvent(e, winStart)));
          // Populate lastPanelOkByPanel from initial events
          const panelOkMap = {};
          for (const ev of data) {
            if (ev.cls === 'PANEL_OK' && ev.client_id && ev.timestamp) {
              // Keep the latest (first) PANEL_OK for each client
              if (!panelOkMap[ev.client_id]) {
                panelOkMap[ev.client_id] = ev.timestamp;
              }
            }
          }
          if (Object.keys(panelOkMap).length > 0) {
            setLastPanelOkByPanel(prev => ({ ...prev, ...panelOkMap }));
          }
          initialLoaded.current = true;
          setAnimationGen(g => g + 1);
          // Enable animations after initial load + delay
          setTimeout(() => { shouldAnimate.current = true; }, 500);
        }
      } catch (_) {}
    }

    pollClients();
    loadInitialLogs();
    const t = setInterval(pollClients, REFRESH_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  // Self-heal Web Push: if the user already granted permission, silently
  // re-subscribe + re-register on every app open. iOS rotates/expires push
  // subscriptions periodically; without this, pushes stop until manual re-activation.
  React.useEffect(() => {
    if (!user) return;
    try {
      if (window.Notification && Notification.permission === 'granted'
          && 'serviceWorker' in navigator && 'PushManager' in window
          && typeof bcEnablePush === 'function') {
        bcEnablePush().catch(() => {});
      }
    } catch (_) {}
  }, [user]);

  // Restore notification badge state from backend on login/page-load
  React.useEffect(() => {
    if (!user) return;
    fetch('/events/notification-state')
      .then(r => r.ok ? r.json() : {})
      .then(state => {
        pairingRef.current = state;
        const winStart = weekWindowStart();
        setEvents(prev => markRestored(prev, state).filter(e => keepEvent(e, winStart)));
        setCountsByClient(state);
        const totals = { alarm: 0, trouble: 0, supervisory: 0 };
        const alarmMap = {};
        const panelOkMap = {};
        for (const [cid, counts] of Object.entries(state)) {
          totals.alarm       += counts.alarm       || 0;
          totals.trouble     += counts.trouble     || 0;
          totals.supervisory += counts.supervisory || 0;
          if (counts.alarm > 0) alarmMap[cid] = counts.alarm;
          if (counts.last_panel_ok_at) panelOkMap[cid] = counts.last_panel_ok_at;
        }
        setNotificationCounts(totals);
        setNewAlarmsByClient(alarmMap);
        if (Object.keys(panelOkMap).length > 0) setLastPanelOkByPanel(prev => ({ ...prev, ...panelOkMap }));
      })
      .catch(() => {});
  }, [user]);

  // Keep lastPanelOkRef in sync so the prune interval can read current value
  React.useEffect(() => { lastPanelOkRef.current = lastPanelOkByPanel; }, [lastPanelOkByPanel]);

  const UNRESOLVED_CLASSES = new Set(['FIRE', 'ALARM', 'TROUBLE', 'SUPERVISORY']);
  const UNRESOLVED_MAX_DAYS = 30;  // as UNRESOLVED_MAX_AGE in app/routes/logs.py
  // Brands whose PANEL OK tracking pairs each alert with its restore (the
  // _TRACKERS of app/panel_ok.py): their restored alerts count as resolved.
  const PAIRED_BRANDS = new Set(['vigilant', 'kidde', 'hochiki', 'notifier_3030', 'generic_cid', 'multi_cid', 'mircom', 'mircom-fx-3500']);

  // Events with the alerts whose restore came marked `restored`: flagged so
  // by /logs, or, per the notification state, every alert of a paired panel
  // since its last PANEL OK (ids paired_from..paired_until) that isn't still
  // active. They stay in the list but count as resolved (eventResolved in
  // monitor.jsx), like the ones a PANEL OK followed: the badges count only
  // the active ones, the alerts the operator has to look at.
  function markRestored(list, state) {
    return list.map(e => {
      if (e.restored) return e;
      const st = state && state[e.client_id];
      if (!st || !st.active_ids || !UNRESOLVED_CLASSES.has(e.cls) || typeof e.id !== 'number') return e;
      const restored = e.id >= st.paired_from && e.id <= st.paired_until && !st.active_ids.includes(e.id);
      return restored ? { ...e, restored: true } : e;
    });
  }

  // A paired panel's state again after one of its events arrived that may be
  // a restore: its badge counts and which alerts are still active.
  const refreshTimers = React.useRef({});
  function scheduleRefresh(cid) {
    clearTimeout(refreshTimers.current[cid]);
    refreshTimers.current[cid] = setTimeout(() => {
      fetch(`/events/notification-state?client_id=${encodeURIComponent(cid)}`)
        .then(r => r.ok ? r.json() : null)
        .then(state => state && state[cid] && applyPanelState(cid, state[cid]))
        .catch(() => {});
    }, 1500);
  }

  function applyPanelState(cid, entry) {
    pairingRef.current = { ...pairingRef.current, [cid]: entry };
    setEvents(prev => markRestored(prev, { [cid]: entry }));
    setCountsByClient(prev => {
      const old = prev[cid] || { alarm: 0, trouble: 0, supervisory: 0 };
      setNotificationCounts(g => ({
        alarm:       Math.max(0, g.alarm       - (old.alarm       || 0) + (entry.alarm       || 0)),
        trouble:     Math.max(0, g.trouble     - (old.trouble     || 0) + (entry.trouble     || 0)),
        supervisory: Math.max(0, g.supervisory - (old.supervisory || 0) + (entry.supervisory || 0)),
      }));
      setNewAlarmsByClient(a => ({ ...a, [cid]: entry.alarm || 0 }));
      return { ...prev, [cid]: entry };
    });
  }

  // The events the dashboard keeps, the same ones /logs sends: the moving
  // week, plus older alerts still unresolved (no PANEL_OK followed, not
  // restored), up to UNRESOLVED_MAX_DAYS back. Live events used to drop those
  // older alerts at the first message.
  function keepEvent(e, winStart) {
    const ts = e.timestamp ? new Date(e.timestamp) : null;
    if (!ts) return false;
    if (ts >= winStart) return true;                     // within window — always keep
    if (!UNRESOLVED_CLASSES.has(e.cls) || e.restored) return false;   // old resolved/non-alarm — drop
    if (Date.now() - ts > UNRESOLVED_MAX_DAYS * 864e5) return false;
    const lastOk = lastPanelOkRef.current[e.client_id];
    return !lastOk || ts > new Date(lastOk);             // keep if no PANEL_OK after it
  }

  // Hourly prune: drop events that slide out of what the dashboard keeps.
  React.useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      const winStart = weekWindowStart();
      setEvents(prev => prev.filter(e => keepEvent(e, winStart)));
    }, 60 * 60 * 1000); // every hour
    return () => clearInterval(t);
  }, [user]);

  // WebSocket for real-time event push
  React.useEffect(() => {
    if (!user) return;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${proto}//${location.host}/ws/events?classification=FIRE,ALARM,TROUBLE,SUPERVISORY,STATE,PANEL_OK`;
    let ws = null;
    let reconnectTimer = null;
    let dead = false;

    function connect() {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => { setLiveState('live'); };
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          // Assign stable id if missing
          if (!ev.id) ev.id = `ws-${Date.now()}-${Math.random().toString(36).slice(2,6)}`;
          if (initialLoaded.current) ev.isNew = true;
          const winStart = weekWindowStart();
          setEvents(prev => [ev, ...prev].filter(e => keepEvent(e, winStart)));
          setLastUpdate(Date.now());
          if (initialLoaded.current) playBeep();
          // Handle badge updates only after initial load
          if (initialLoaded.current) {
            if (ev.cls === 'PANEL_OK' && ev.client_id) {
              // Track last PANEL_OK timestamp for this panel (to mark older events as resolved)
              setLastPanelOkByPanel(prev => ({ ...prev, [ev.client_id]: ev.timestamp || Date.now() }));
              // Clear badges for this specific panel
              setCountsByClient(prev => {
                const perClient = prev[ev.client_id] || { alarm: 0, trouble: 0, supervisory: 0 };
                setNotificationCounts(g => ({
                  alarm:       Math.max(0, g.alarm       - (perClient.alarm       || 0)),
                  trouble:     Math.max(0, g.trouble     - (perClient.trouble     || 0)),
                  supervisory: Math.max(0, g.supervisory - (perClient.supervisory || 0)),
                }));
                setNewAlarmsByClient(a => ({ ...a, [ev.client_id]: 0 }));
                const next = { ...prev };
                delete next[ev.client_id];
                return next;
              });
            } else {
              // Possibly a restore of a paired panel's alert: count again.
              if (!UNRESOLVED_CLASSES.has(ev.cls) && PAIRED_BRANDS.has(ev.brand) && ev.client_id) {
                scheduleRefresh(ev.client_id);
              }
              const cat = classifyCategory(ev.cls);
              if (cat) {
                setNotificationCounts(prev => ({ ...prev, [cat]: prev[cat] + 1 }));
                if (ev.client_id) {
                  setCountsByClient(prev => ({
                    ...prev,
                    [ev.client_id]: {
                      ...(prev[ev.client_id] || { alarm: 0, trouble: 0, supervisory: 0 }),
                      [cat]: ((prev[ev.client_id] || {})[cat] || 0) + 1,
                    },
                  }));
                }
                if (cat === 'alarm' && ev.client_id) {
                  setNewAlarmsByClient(prev => ({ ...prev, [ev.client_id]: (prev[ev.client_id] || 0) + 1 }));
                }
              }
            }
          }
        } catch (_) {}
      };
      ws.onclose = () => {
        if (!dead) {
          setLiveState('reconnecting');
          reconnectTimer = setTimeout(connect, 3000);
        }
      };
      ws.onerror = () => { setLiveState('offline'); };
    }

    // iOS suspends backgrounded PWAs and leaves the socket half-open (readyState
    // stays OPEN but no data flows). On every return to foreground, tear the old
    // socket down and open a fresh one so live events resume without a restart.
    function onResume() {
      if (dead || document.visibilityState !== 'visible') return;
      try { if (ws) { ws.onclose = null; ws.onerror = null; ws.close(); } } catch (_) {}
      clearTimeout(reconnectTimer);
      setLiveState('reconnecting');
      connect();
    }
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('online', onResume);
    window.addEventListener('pageshow', onResume);

    connect();
    return () => {
      dead = true;
      clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('online', onResume);
      window.removeEventListener('pageshow', onResume);
      ws && ws.close();
    };
  }, [user]);

  const [sequencesByClient, setSequencesByClient] = React.useState({});
  const [lastSeqResult, setLastSeqResult] = React.useState(null);

  // Load commands + sequences when selected panel changes
  React.useEffect(() => {
    if (!selected || !user) return;
    fetch(`/clients/${selected}/commands`)
      .then(r => r.ok ? r.json() : [])
      .then(cmds => setCommandsByClient(prev => ({ ...prev, [selected]: cmds })))
      .catch(() => {});
    fetch(`/clients/${selected}/sequences`)
      .then(r => r.ok ? r.json() : [])
      .then(seqs => setSequencesByClient(prev => ({ ...prev, [selected]: seqs })))
      .catch(() => {});
  }, [selected, user]);

  const counts = notificationCounts;

  function handleLogin(u) { setUser(u); }

  function handleHeaderFilter(cat) {
    setAnimationGen(g => g + 1);
    if (categoryFilter === cat) {
      // Clicking active badge → deselect
      setCategoryFilter(null);
      setPanelFilter(null);
    } else {
      // New category → apply filter to all panels
      setCategoryFilter(cat);
      setPanelFilter(null);
    }
  }

  async function handleLogout() {
    try { await fetch('/auth/logout', { method: 'POST' }); } catch (_) {}
    setUser(null);
    setClients([]);
    setEvents([]);
    setCategoryFilter(null);
    setSearch('');
    setSection('monitor');
  }

  function handleReconnect() {
    setLiveState('reconnecting');
    fetch('/clients')
      .then(r => r.ok ? (setLiveState('live'), r.json()) : Promise.reject())
      .then(data => setClients(data))
      .catch(() => setLiveState('offline'));
  }

  function handleResetNotifications() {
    setNotificationCounts({ alarm: 0, trouble: 0, supervisory: 0 });
    setNewAlarmsByClient({});
    setCountsByClient({});
    setCategoryFilter(null);
    setPanelFilter(null);
    setEvents(prev => prev.map(e => ({ ...e, isNew: false })));
    shouldAnimate.current = false;

    setAnimationGen(g => g + 1);
    setTimeout(() => { shouldAnimate.current = true; }, 300);
  }

  async function handleSendSequence(seq) {
    try {
      const res = await fetch('/clients/send-sequence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: selected, sequence_id: seq.id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setLastSeqResult({ ok:false, msg: data.detail || `${seq.name} falló` });
      } else {
        setLastSeqResult({ ok:true, msg:`${seq.name} iniciada · esperando (${seq.timeout_seconds}s)…` });
      }
    } catch (_) {
      setLastSeqResult({ ok:false, msg:`${seq.name} · error de red` });
    }
    setTimeout(() => setLastSeqResult(null), 5000);
  }

  async function handleSendCommand(cmd) {
    try {
      const res = await fetch('/clients/send-command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: selected, command_id: cmd.id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setLastCmdResult({ ok:false, msg: data.detail || `${cmd.name} falló` });
      } else {
        setLastCmdResult({ ok:true, msg:`${cmd.name} enviado` });
      }
    } catch (_) {
      setLastCmdResult({ ok:false, msg:`${cmd.name} · error de red` });
    }
    setTimeout(() => setLastCmdResult(null), 3500);
  }

  // Special pages: invite + password reset — shown without auth
  const _pathname = window.location.pathname;
  const _params   = new URLSearchParams(window.location.search);
  if (_pathname === '/accept-invite') {
    return <InviteOverlay token={_params.get('token')} onLogin={handleLogin}/>;
  }
  if (_pathname === '/reset-password') {
    return <ResetPasswordOverlay oobCode={_params.get('oobCode')}/>;
  }

  if (!authChecked) {
    return <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:BC.bg, color:BC.inkFaint, fontSize:13 }}>
      <Icon name="refresh" size={16} style={{ animation:'bc-spin .7s linear infinite', marginRight:8 }}/> Cargando…
    </div>;
  }

  if (!user) {
    return <>
      <LoginOverlay onLogin={handleLogin}/>
    </>;
  }

  // Simplified panel + command console (auto on phones, or forced via the menu toggles)
  if (showSimplified) {
    return <MobileView user={user} clients={clients} events={events} counts={counts}
      selected={selected} setSelected={setSelected}
      commandsByClient={commandsByClient} onSendCommand={handleSendCommand}
      lastCmdResult={lastCmdResult} onLogout={handleLogout}
      onResetNotifications={handleResetNotifications} liveState={liveState}
      newAlarmsByClient={newAlarmsByClient}
      panelOrder={panelOrder}
      onExitMobile={() => setViewPref('full')}/>;
  }

  const view = section === 'monitor'
    ? <MonitorView user={user} clients={clients} events={events} simpleText={!!user.simple_view} includeAddresses={!!user.include_addresses}
        commandsByClient={commandsByClient} sequencesByClient={sequencesByClient}
        selected={selected} setSelected={setSelected}
        categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
        panelFilter={panelFilter} setPanelFilter={setPanelFilter}
        search={search} setSearch={setSearch}
        onSendCommand={handleSendCommand} lastCmdResult={lastCmdResult}
        onSendSequence={handleSendSequence} lastSeqResult={lastSeqResult}
        density={density} shouldAnimate={shouldAnimate.current}
        newAlarmsByClient={newAlarmsByClient}
        countsByClient={countsByClient}
        lastPanelOkByPanel={lastPanelOkByPanel}
        animationGen={animationGen}
        panelOrder={panelOrder} onPanelOrderChange={setPanelOrder}
        sidebarOpen={sidebarOpen} onCloseSidebar={() => setSidebarOpen(false)}/>
    : section === 'stream'
    ? <StreamView clients={clients} events={events} simpleText={!!user.simple_view} includeAddresses={!!user.include_addresses}
        categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
        panelFilter={panelFilter} setPanelFilter={setPanelFilter}
        search={search} setSearch={setSearch}
        density={density} shouldAnimate={shouldAnimate.current}
        countsByClient={countsByClient}
        lastPanelOkByPanel={lastPanelOkByPanel}
        animationGen={animationGen}
        panelOrder={panelOrder}/>
    : <AdminView user={user}/>;

  const connectedCount = clients.filter(c => c.alive_status === 'connected').length;

  return (
    <div className="bc-mobile-root" style={{ display:'flex', flexDirection:'column', background:BC.bg }}>
      <AppHeader user={user} counts={counts} animationGen={animationGen} shouldAnimate={shouldAnimate.current}
        onLogout={handleLogout}
        onReconnect={handleReconnect}
        onResetNotifications={handleResetNotifications}
        liveState={liveState}
        onFilter={handleHeaderFilter} activeFilter={categoryFilter}
        section={section} onSection={setSection}
        density={density} onDensity={setDensity}
        onSidebarToggle={() => setSidebarOpen(o => !o)}
        onEnterMobile={() => setViewPref('simple')}/>
      {view}
      <StatusBar panels={clients.length} connected={connectedCount} events={events.length}
        filter={categoryFilter} onClearFilter={() => { setCategoryFilter(null); setPanelFilter(null); }}
        lastUpdate={lastUpdate}/>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App/>);
